/**
 * @fileoverview shims the datadog logging functions
 * @note use these tools for performance timing and feature health; we intend to for this
 *   to supersede `appboy#logCustomEvent` in order to shunt all data into DataDog
 */

import { requestIdleCallback } from "@lib/requestIdleCallback";

/**
 * We're loading the script for datadog logger through a head <script> tags selectively
 * rather than importing  modules from npm, so this is what we're doing instead:
 *
 * This will expose the following functions on the exported logger object:
 * - debug
 * - info
 * - warn
 * - error
 * - userAction
 *
 * Per the datadog docs:
 * - the first arg for these functions is a string
 * - the second arg is an object to give the logged event more context
 *
 * We include a `timing` property on all context objects to do synthetic performance timing.
 * This figure is the difference between the initial <head> parsing and the time the event was
 * fired, in milliseconds. (we expect DataDog to include this as an intrinsic feature in the near
 * future, so this is a temporary solution). Because we load datadog asynchronously, we also
 * wrap all calls under each library's respective onReady function.
 *
 * This combines the synthetic logging functions with the RUM tooling's `addAction`
 *
 * @note All analytics calls should be done on idleCallback so as to not block initial render and
 * count against the AJAX / fetch limits of the browser
 */

type Status = "debug" | "info" | "warn" | "error";
type Context = { [key: string]: any };
type OnReady = (callback: () => void) => void;

// These are injected in dashboard.html.erb.
declare global {
  interface Window {
    __SYNTHETIC_LOAD_TIME__?: number;
    DD_RUM?: {
      onReady: OnReady;
      addAction?: (action: string, context?: Context) => void;
      addError?: (error: unknown, context?: Context) => void;
    };
    DD_LOGS?: {
      onReady: OnReady;
      logger?: {
        log(message: string, context?: Context, status?: Status): void;
      };
    };
  }
}

function safeLog(status: Status, message: string, context?: Context) {
  if (!window.DD_LOGS) {
    return;
  }
  const now = Date.now();
  window.DD_LOGS.onReady(() => {
    requestIdleCallback(() => {
      window.DD_LOGS?.logger?.log(message, addTimingToContext(context, now), status);
    });
  });
}

function safeAddAction(action: string, context?: Context) {
  if (!window.DD_RUM) {
    return;
  }
  // Ignore any headless browser such as integration and e2e tests
  if (window.navigator.userAgent.includes("HeadlessChrome")) {
    return;
  }
  const now = Date.now();
  window.DD_RUM.onReady(() => {
    requestIdleCallback(() => {
      window.DD_RUM?.addAction?.(action, addTimingToContext(context, now));
    });
  });
}

function safeAddError(error: unknown, context?: Context) {
  if (!window.DD_RUM) {
    return;
  }
  if (window.navigator.userAgent.includes("HeadlessChrome")) {
    return;
  }
  window.DD_RUM.onReady(() => {
    window.DD_RUM?.addError?.(error, context);
  });
}

function addTimingToContext(context: Context | undefined, now: number) {
  if (window.__SYNTHETIC_LOAD_TIME__ === undefined) {
    return context;
  }
  context ??= {};
  context.timing = now - window.__SYNTHETIC_LOAD_TIME__;
  return context;
}

function createStatusLog(status: Status) {
  return (message: string, context?: Context) => {
    safeLog(status, message, context);
  };
}

export default {
  debug: createStatusLog("debug"),
  info: createStatusLog("info"),
  warn: createStatusLog("warn"),
  error: createStatusLog("error"),
  userAction: safeAddAction,
  rumError: safeAddError,
} as const;
